import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ItineraryEditor = ({ itinerary, onUpdateItinerary }) => {
  const [editingActivity, setEditingActivity] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  const handleEditActivity = (dayIndex, activityIndex, field, value) => {
    const updatedItinerary = [...itinerary];
    updatedItinerary[dayIndex].activities[activityIndex][field] = value;
    onUpdateItinerary(updatedItinerary);
  };

  const handleAddActivity = (dayIndex) => {
    const updatedItinerary = [...itinerary];
    const newActivity = {
      id: Date.now(),
      time: "12:00 PM",
      title: "New Activity",
      description: "Add activity description",
      location: "Location",
      duration: "1 hour",
      notes: ""
    };
    updatedItinerary?.[dayIndex]?.activities?.push(newActivity);
    onUpdateItinerary(updatedItinerary);
  };

  const handleRemoveActivity = (dayIndex, activityIndex) => {
    const updatedItinerary = [...itinerary];
    updatedItinerary?.[dayIndex]?.activities?.splice(activityIndex, 1);
    onUpdateItinerary(updatedItinerary);
  };

  const handleDragStart = (e, dayIndex, activityIndex) => {
    setDraggedItem({ dayIndex, activityIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetDayIndex, targetActivityIndex) => {
    e?.preventDefault();
    if (!draggedItem) return;

    const updatedItinerary = [...itinerary];
    const draggedActivity = updatedItinerary?.[draggedItem?.dayIndex]?.activities?.[draggedItem?.activityIndex];
    
    // Remove from original position
    updatedItinerary?.[draggedItem?.dayIndex]?.activities?.splice(draggedItem?.activityIndex, 1);
    
    // Insert at new position
    updatedItinerary?.[targetDayIndex]?.activities?.splice(targetActivityIndex, 0, draggedActivity);
    
    onUpdateItinerary(updatedItinerary);
    setDraggedItem(null);
  };

  const ActivityCard = ({ activity, dayIndex, activityIndex, isEditing, onEdit }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, dayIndex, activityIndex)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, dayIndex, activityIndex)}
      className="bg-card border border-border rounded-organic p-4 hover:shadow-organic-md transition-organic cursor-move"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-primary rounded-full"></div>
          {isEditing ? (
            <Input
              type="time"
              value={activity?.time}
              onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'time', e?.target?.value)}
              className="w-24"
            />
          ) : (
            <span className="font-body font-body-medium text-sm text-primary">
              {activity?.time}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onEdit(isEditing ? null : `${dayIndex}-${activityIndex}`)}
            className="p-1 text-muted-foreground hover:text-foreground transition-organic"
          >
            <Icon name={isEditing ? "Check" : "Edit2"} size={16} />
          </button>
          <button
            onClick={() => handleRemoveActivity(dayIndex, activityIndex)}
            className="p-1 text-muted-foreground hover:text-error transition-organic"
          >
            <Icon name="Trash2" size={16} />
          </button>
          <div className="cursor-move p-1 text-muted-foreground">
            <Icon name="GripVertical" size={16} />
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {isEditing ? (
          <Input
            value={activity?.title}
            onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'title', e?.target?.value)}
            className="font-heading font-heading-semibold"
          />
        ) : (
          <h4 className="font-heading font-heading-semibold text-lg text-foreground">
            {activity?.title}
          </h4>
        )}
        
        {isEditing ? (
          <textarea
            value={activity?.description}
            onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'description', e?.target?.value)}
            className="w-full p-2 border border-border rounded-organic-sm resize-none"
            rows={2}
          />
        ) : (
          <p className="font-body text-muted-foreground">{activity?.description}</p>
        )}
        
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="MapPin" size={14} />
            {isEditing ? (
              <Input
                value={activity?.location}
                onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'location', e?.target?.value)}
                className="w-32"
              />
            ) : (
              <span>{activity?.location}</span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={14} />
            {isEditing ? (
              <Input
                value={activity?.duration}
                onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'duration', e?.target?.value)}
                className="w-20"
              />
            ) : (
              <span>{activity?.duration}</span>
            )}
          </div>
        </div>
        
        {(activity?.notes || isEditing) && (
          <div className="pt-2 border-t border-border">
            {isEditing ? (
              <textarea
                value={activity?.notes}
                onChange={(e) => handleEditActivity(dayIndex, activityIndex, 'notes', e?.target?.value)}
                placeholder="Add personal notes..."
                className="w-full p-2 border border-border rounded-organic-sm resize-none text-sm"
                rows={2}
              />
            ) : (
              <p className="font-body text-sm text-muted-foreground italic">
                {activity?.notes}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {itinerary?.map((day, dayIndex) => (
        <div key={day?.date} className="bg-muted/30 rounded-organic-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading font-heading-bold text-xl text-foreground mb-1">
                Day {day?.day}
              </h3>
              <p className="font-body text-muted-foreground">
                {new Date(day.date)?.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAddActivity(dayIndex)}
              iconName="Plus"
              iconPosition="left"
            >
              Add Activity
            </Button>
          </div>
          
          <div className="space-y-4">
            {day?.activities?.map((activity, activityIndex) => (
              <ActivityCard
                key={activity?.id}
                activity={activity}
                dayIndex={dayIndex}
                activityIndex={activityIndex}
                isEditing={editingActivity === `${dayIndex}-${activityIndex}`}
                onEdit={setEditingActivity}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ItineraryEditor;